import React, { PureComponent } from 'react';

import { injectIntl, InjectedIntl } from 'react-intl';

import Spinner from '@atlaskit/spinner';

import Announcer from 'src/components/announcer';
import { User, Team } from 'src/components/types';
import {
  SearchResponse,
  CodeSearchResult,
  isSearchError,
  isSearchNotInstalled,
} from 'src/types/search';

import * as styles from '../empty-state/empty-state.style';
import { OptInSourceView } from '../opt-in/opt-in-source-view';
import QuerySubstitutedWarning from '../query-substituted-warning/query-substituted-warning';
import { Results } from '../results';
import { ResultsPaginationProps } from '../results-pagination/results-pagination';
import { SearchErrorState } from '../search-error/search-error';
import { SearchUnknownError } from '../search-error/search-unknown-error';

import { NoResultsState } from './no-results-state';
import messages from './search-results.i18n';

export type ResultsSourceViewProps = {
  isLoading: boolean;
  response: SearchResponse;
  syntaxError: 'OPERATOR' | 'EXTENSION' | null;
  query: string;
  account: User | Team;
  /* eslint react/no-unused-prop-types: "warn" */
  count: number;
  intl: InjectedIntl;
} & ResultsPaginationProps;

class ResultsSourceView extends PureComponent<ResultsSourceViewProps> {
  render() {
    const {
      account,
      currentPage,
      isLoading,
      onSetPage,
      pages,
      query,
      response,
      showPages,
      syntaxError,
      intl,
    } = this.props;

    if (isLoading) {
      return (
        <styles.Centered>
          <Spinner size="large" />
        </styles.Centered>
      );
    }

    if (response && isSearchNotInstalled(response)) {
      return <OptInSourceView />;
    }

    if (response && isSearchError(response)) {
      return <SearchErrorState response={response} />;
    }

    if (!response || !response.values) {
      return <SearchUnknownError />;
    }

    const values: CodeSearchResult[] = response.values || [];
    if (!values.length) {
      return (
        <div>
          <NoResultsState
            query={query}
            syntaxError={syntaxError}
            accountUuid={account.uuid}
          />
          <Announcer
            key={Date.now()} // force-render component
            text={intl.formatMessage(messages.announceNoCodeSearchResults)}
          />
        </div>
      );
    }

    const querySubstituted = response.query_substituted;
    const totalPages = pages.length;

    return (
      <div>
        {querySubstituted && <QuerySubstitutedWarning />}
        <Results
          displayRepoName={false}
          codeSearchResults={values}
          currentPage={currentPage}
          onSetPage={onSetPage}
          showPages={showPages}
          pages={pages}
          account={account}
          directoryPathClickable={false}
        />
        <Announcer
          text={intl.formatMessage(
            totalPages > 1
              ? messages.announcePaginatedCodeSearchResults
              : messages.announceCodeSearchResults,
            {
              count: values.length,
              totalPages,
              currentPage,
            }
          )}
        />
      </div>
    );
  }
}

export default injectIntl(ResultsSourceView);
